// src/components/Courses/Shared/CourseHeader.jsx
import React from "react";

const CourseHeader = ({
    title = "Information",
    highlight = "Technology",
    subtitle = "Innovate. Create. Transform.",
    backgroundImage = "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=2072&q=80",
}) => {
    return (
        <div className="relative w-full h-[450px] md:h-[550px] lg:h-[650px] overflow-hidden">
            {/* Background Image with zoom animation */}
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-110 animate-slow-zoom"
                style={{
                    backgroundImage: `linear-gradient(rgba(93, 4, 112, 0.6), rgba(93, 4, 112, 0.4)), url('${backgroundImage}')`,
                }}
            />

            {/* Net pattern overlay with fade in animation */}
            <div className="absolute inset-0 bg-net-pattern opacity-20 animate-fade-in" />

            {/* Gradient overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-custom-pink/70 via-custom-pink/50 to-transparent animate-fade-in-delayed" />

            {/* Content Container */}
            <div className="relative h-full flex items-center justify-start">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-6xl">
                        {/* Main Title */}
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold text-white leading-tight whitespace-nowrap animate-slide-in-left">
                            {title}{" "}
                            <span className="text-gold animate-glow">{highlight}</span>
                        </h1>

                        {/* Subtitle */}
                        <div className="mt-6 md:mt-8 animate-slide-in-up">
                            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/90 font-light leading-relaxed animate-fade-in-up">
                                {subtitle}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute bottom-0 right-0 w-32 h-32 md:w-48 md:h-48 lg:w-64 lg:h-64 animate-float">
                <div className="absolute inset-0 bg-gold/20 rounded-full blur-3xl animate-pulse-slow" />
            </div>

            <div className="absolute top-1/4 right-1/4 w-16 h-16 md:w-24 md:h-24 lg:w-32 lg:h-32 animate-bounce-slow">
                <div className="absolute inset-0 border-2 border-gold/40 rounded-full animate-spin-slow" />
            </div>

            {/* Floating particles */}
            <div className="absolute top-1/3 left-1/4 w-8 h-8 bg-gold/30 rounded-full animate-float-delayed blur-sm" />
            <div className="absolute top-2/3 right-1/3 w-6 h-6 bg-white/20 rounded-full animate-float-reverse blur-sm" />
            <div className="absolute top-1/2 left-1/6 w-4 h-4 bg-gold/40 rounded-full animate-pulse-delayed blur-sm" />

            {/* Bottom gradient fade */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/80 to-transparent animate-slide-up" />

            {/* Animations (same as your IT header) */}
            <style jsx>{`
        @keyframes slowZoom {
          0% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1.1);
          }
        }
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes fadeInDelayed {
          0%,
          30% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes slideInLeft {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideInUp {
          0% {
            transform: translateY(50px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes fadeInUp {
          0% {
            transform: translateY(30px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes glow {
          0%,
          100% {
            text-shadow: 0 0 20px rgba(252, 169, 3, 0.5);
          }
          50% {
            text-shadow:
              0 0 40px rgba(252, 169, 3, 0.8),
              0 0 60px rgba(252, 169, 3, 0.6);
          }
        }
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        @keyframes floatDelayed {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }
        @keyframes floatReverse {
          0%,
          100% {
            transform: translateY(-10px);
          }
          50% {
            transform: translateY(10px);
          }
        }
        .animate-slow-zoom {
          animation: slowZoom 20s ease-in-out infinite alternate;
        }
        .animate-fade-in {
          animation: fadeIn 1.5s ease-out;
        }
        .animate-fade-in-delayed {
          animation: fadeInDelayed 2s ease-out;
        }
        .animate-slide-in-left {
          animation: slideInLeft 1s ease-out;
        }
        .animate-slide-in-up {
          animation: slideInUp 1s ease-out 0.3s both;
        }
        .animate-fade-in-up {
          animation: fadeInUp 1s ease-out 0.5s both;
        }
        .animate-glow {
          animation: glow 3s ease-in-out infinite;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: floatDelayed 8s ease-in-out infinite 2s;
        }
        .animate-float-reverse {
          animation: floatReverse 7s ease-in-out infinite 1s;
        }
        .animate-pulse-slow {
          animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .animate-pulse-delayed {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite 1.5s;
        }
        .animate-bounce-slow {
          animation: bounce 3s infinite;
        }
        .animate-spin-slow {
          animation: spin 15s linear infinite;
        }
        .animate-slide-up {
          animation: slideInUp 1.2s ease-out 0.8s both;
        }
      `}</style>
        </div>
    );
};

export default CourseHeader;
